import React, { useState, useEffect, useRef } from "react";
import styles from "./ShippingStack.module.scss";
import { getCDNImageUrl } from "@/utils/Common";

const TABS = [
  {
    tab: "Live Shipment Tracking",
    heading: "Real-Time Shipment Tracking. End to End.",
    body: "From the manufacturing facility to your warehouse door, Elchemy gives you live shipment visibility on a global map. Track international routes (e.g. USA → Italy) and domestic legs simultaneously. Every milestone — Packed, Shipped, In Transit, Arrived — is logged and displayed in real time, so your operations team always has the answers before they're asked.",
    image: "shipping-map.webp",
  },
  {
    tab: "Visual Evidence for Every Shipment",
    heading: "Visual Proof of Every Shipment. Built Right In.",
    body: "Elchemy captures and shares stuffing images, photographic proof from inside the container with every order. Labelled drums, sealed pallets, and container-load documentation are attached directly to your order record, giving your quality and logistics teams the visual confidence they need without a single phone call.",
    image: "shipping-gallery.webp",
  },
  {
    tab: "Documents Done Right",
    heading: "Document Management for Chemical Trade",
    body: "From TDS and SDS to COA and export documentation: Elixir organises, tracks, and delivers every critical chemical trade document automatically. No chasing. No missing paperwork. Just clean, compliant chemical manufacturing documentation, every time. This is technology in the chemical industry working the way it should.",
    image: "shipping-documents.webp",
  },
];

const AUTO_SCROLL_MS = 4000;

function ShippingStack() {
  const [active, setActive] = useState(0);
  const current = TABS[active];

  // Mobile carousel: track the active slide and auto-advance. Scrolling the
  // track (swipe) updates the active dot; the dots also drive the scroll.
  const [slide, setSlide] = useState(0);
  const trackRef = useRef(null);
  const isUserScrolling = useRef(false);

  const scrollToSlide = (index) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[index];
    if (card) {
      track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: "smooth" });
    }
  };

  // Auto-advance the mobile carousel.
  useEffect(() => {
    const id = setInterval(() => {
      setSlide((prev) => {
        const next = (prev + 1) % TABS.length;
        scrollToSlide(next);
        return next;
      });
    }, AUTO_SCROLL_MS);
    return () => clearInterval(id);
  }, []);

  // Keep the active dot in sync when the user swipes the track.
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    isUserScrolling.current = true;
    const idx = Math.round(track.scrollLeft / track.clientWidth);
    if (idx !== slide) setSlide(idx);
  };

  const handleDotClick = (index) => {
    setSlide(index);
    scrollToSlide(index);
  };

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.title}>
          The Smarter Shipping Stack for Chemical Procurement
        </h2>

        {/* Desktop: tabbed panel */}
        <div className={styles.panel}>
          <div className={styles.tabs} role="tablist">
            {TABS.map((t, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={active === i}
                className={`${styles.tab} ${active === i ? styles.tabActive : ""}`}
                onClick={() => setActive(i)}
              >
                {t.tab}
              </button>
            ))}
          </div>

          <span className={styles.divider} aria-hidden="true" />

          <div className={styles.content}>
            <h3 className={styles.contentHeading}>{current.heading}</h3>
            <p className={styles.contentBody}>{current.body}</p>
            <div className={styles.mapWrapper}>
              <img
                src={getCDNImageUrl(current.image)}
                alt={current.heading}
                className={styles.map}
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Mobile: auto-scrolling carousel of dark cards */}
        <div className={styles.carousel}>
          <div
            className={styles.carouselTrack}
            ref={trackRef}
            onScroll={handleScroll}
          >
            {TABS.map((t, i) => (
              <article className={styles.card} key={i}>
                <h3 className={styles.cardHeading}>{t.heading}</h3>
                <span className={styles.cardDivider} aria-hidden="true" />
                <p className={styles.cardBody}>{t.body}</p>
                <div className={styles.cardImageWrapper}>
                  <img
                    src={getCDNImageUrl(t.image)}
                    alt={t.heading}
                    className={styles.cardImage}
                    loading="lazy"
                  />
                </div>
              </article>
            ))}
          </div>

          <div className={styles.dots} role="tablist" aria-label="Shipping features">
            {TABS.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={slide === i}
                aria-label={`Feature ${i + 1}`}
                className={`${styles.dot} ${slide === i ? styles.dotActive : ""}`}
                onClick={() => handleDotClick(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ShippingStack;
